import { Injectable, inject } from "@angular/core";
import { catchError, map, switchMap, takeWhile, withLatestFrom } from "rxjs";

import { Actions, createEffect, ofType } from "@ngrx/effects";

// Store
import { Store } from "@ngrx/store";
import * as SportsbookActions from "../../../stores/sportsbook/sportsbook.actions";
import { SportsbookFacade } from "../../../stores/sportsbook/sportsbook.facade";
import { SportsbookState } from "../../../stores/sportsbook/sportsbook.reducers";

// Services
import { BufferFacade } from "@shared-providers/facades";
import { SportTrackPollingService } from "@sports-services/index";
import { SPORTS_NAVIGATION_SERVICE_ERROR_CODES } from "@sports-services/sports.navigation.service";

// Utils
import { SportLiveMarketService } from "@sports-services/sports.live-market.service";
import { SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION } from "@sports-services/sports.navigation.service/sports.navigations.constants";
import { SportsbookUtils } from "../../../../sports/utils/sportsbook.utils";

@Injectable({ providedIn: "root" })
export class SportsbookLiveMarketEffects {
  // Services
  actions$ = inject(Actions);
  bufferFacade = inject(BufferFacade);
  sportLiveMarketService = inject(SportLiveMarketService);

  // Utils
  sportBookUtils = inject(SportsbookUtils);
  store = inject(Store<SportsbookState>);
  sportsbookFacade = inject(SportsbookFacade);
  sportTrackPollingService = inject(SportTrackPollingService);

  timer = {
    live: "live",
    regular: "regular",
    highlights: "highlights",
  };

  // LIVE MARKET
  initLiveMarket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.initLiveMarket),
      map((payload) => {
        this.sportTrackPollingService.startLiveMarketPagePolling();
        const { market, isBetsenseEnable } = payload;
        return SportsbookActions.fetchLiveMarket({
          parentId: market.NodeId,
          categoryInfoId: "99",
          leagueName: market.LeagueName,
          market,
          isBetsenseEnable,
        });
      }),
    ),
  );

  fetchLiveMarket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.fetchLiveMarket),
      switchMap(({ type, ...payload }) => {
        const { isBetsenseEnable, ...fetchMarketLiveParams } = payload;
        const params = {
          parentId: fetchMarketLiveParams.parentId,
          categoryInfoId: fetchMarketLiveParams.categoryInfoId,
          sportHandle: fetchMarketLiveParams.market.SportHandle,
          leagueName: fetchMarketLiveParams.leagueName,
        };
        if (isBetsenseEnable) this.sportsbookFacade.fetchLiveMarketBetsense({ fetchMarketLiveParams });
        return this.bufferFacade
          .newAuxBuffer(
            this.sportLiveMarketService,
            "getGamesLiveEvent2",
            params,
            this.timer.live,
            this.sportTrackPollingService.liveMarketPolling$,
          )
          .pipe(
            withLatestFrom(this.store.select("sportsbook")),
            takeWhile((_) => this.sportTrackPollingService.liveMarketPolling$.getValue()),
            map(([[prevLiveMarketData, currentLiveMarketData], sportsbook]) => {
              const liveMarketPageData = sportsbook["liveMarketData"];
              const fetchMarketLiveParamsComplete = liveMarketPageData.market.LeagueName;
              const { categories, selectedCategory, processedCurrentLiveMarketData } =
                this.sportBookUtils.processLiveMarketDataResponse(
                  currentLiveMarketData,
                  prevLiveMarketData,
                  fetchMarketLiveParams,
                  liveMarketPageData,
                );
              return SportsbookActions.setLiveMarket({
                market: fetchMarketLiveParamsComplete ? fetchMarketLiveParams.market : currentLiveMarketData.Event,
                prevLiveMarketData: prevLiveMarketData,
                currentLiveMarketData: processedCurrentLiveMarketData,
                categories: categories,
                selectedCategory,
              });
            }),
            catchError((e) => {
              if (e.message === "CurrentSelectedCategoryNotExistsInNextCategories") {
                this.sportsbookFacade.setLiveMarketSelectedCategory({
                  selectedCategory: { CategoryId: "99" },
                });
              } else {
                this.sportsbookFacade.setSportsError({
                  errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_POLLING_ERROR,
                  errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_12,
                });
              }
              return [];
            }),
          );
      }),
    ),
  );

  fetchLiveMarketBetsense$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.fetchLiveMarketBetsense),
      switchMap(({ type, fetchMarketLiveParams }) => {
        const { market } = fetchMarketLiveParams;
        const marketLivePageDataNodeId = market?.NodeId;
        return this.bufferFacade
          .newAuxBuffer(
            this.sportLiveMarketService,
            "getBetsenseData",
            marketLivePageDataNodeId,
            this.timer.regular,
            this.sportTrackPollingService.liveMarketPolling$,
          )
          .pipe(
            takeWhile((_) => this.sportTrackPollingService.liveMarketPolling$.getValue()),
            switchMap(([prev, currentBetsenseData]) => {
              const nodeIdList = this.sportBookUtils.getNodeIdList(currentBetsenseData.response.results);
              const params = {
                parentid: fetchMarketLiveParams.parentId,
                categoryInfoId: fetchMarketLiveParams.categoryInfoId,
                leagueName: fetchMarketLiveParams.leagueName,
                sportHandle: fetchMarketLiveParams.market.SportHandle,
                nodeIdList,
              };
              return this.sportLiveMarketService.getGamesLiveEvent2(params).pipe(
                // takeWhile((_) => this.liveMarketPolling$.getValue()),
                map((currentLiveMarketData) => {
                  const { resultOddNodes } = currentLiveMarketData;
                  const updatedBetsenseData = this.sportBookUtils.updateBetsenseData(
                    resultOddNodes,
                    currentBetsenseData.response.results,
                  );
                  return SportsbookActions.setLiveMarketBetsense({
                    betsenseData: updatedBetsenseData,
                  });
                }),
              );
            }),
            catchError((e) => {
              this.sportsbookFacade.setSportsError({
                errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.BETSENSE_POLLING_ERROR,
                errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_13,
              });
              return [];
            }),
          );
      }),
    ),
  );

  setLiveMarketSelectedCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.setLiveMarketSelectedCategory),
      withLatestFrom(this.store.select("sportsbook")),
      map(([{ type, ...payload }, sportsbook]) => {
        const liveMarketPageData = sportsbook["liveMarketData"];
        return SportsbookActions.fetchLiveMarket({
          parentId: liveMarketPageData.market.NodeId,
          categoryInfoId: payload.selectedCategory.CategoryId,
          leagueName: liveMarketPageData.market.LeagueName,
          market: liveMarketPageData.market,
        });
      }),
    ),
  );
}
